<script setup lang="ts">
import { computed } from 'vue'

import { useReportMaintenance } from '../../composables/useReportMaintenance'
import { REPORT_TOOLBAR_ICONS } from '../../configs/reportToolbarIcons'

import { Icon, Menu, type MenuItemOption } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    menuLabel: string
    exportSheetLabel: string
    downloadExcelLabel: string
    fullscreenLabel: string
    exitFullscreenLabel: string
    hideChartLabel?: string
    showChartLabel?: string
    addToDashboardLabel?: string
    disabled?: boolean
    exportSheetBusy?: boolean
    downloadExcelBusy?: boolean
    fullscreen?: boolean
    allowExport?: boolean
    allowAddToDashboard?: boolean
    allowFullscreen?: boolean
    allowHideChart?: boolean
    allowShowChart?: boolean
    maintenanceScheduleId?: number | null | undefined
    clearCacheLabel?: string
    executeAnalysisLabel?: string
  }>(),
  {
    disabled: false,
    exportSheetBusy: false,
    downloadExcelBusy: false,
    fullscreen: false,
    allowExport: true,
    allowAddToDashboard: false,
    allowFullscreen: true,
    allowHideChart: false,
    allowShowChart: false,
    addToDashboardLabel: '',
    hideChartLabel: '',
    showChartLabel: '',
    clearCacheLabel: '',
    executeAnalysisLabel: '',
  },
)

const emit = defineEmits<{
  exportSheet: []
  downloadExcel: []
  toggleFullscreen: []
  hideChart: []
  showChart: []
  addToDashboard: []
}>()

// `undefined` means the host has no maintenance capability at all. `null` is a report host whose
// schedule has not resolved; instantiate the shared policy so it reacts when the id appears.
const maintenance =
  props.maintenanceScheduleId === undefined
    ? null
    : useReportMaintenance(() => props.maintenanceScheduleId)

const items = computed<MenuItemOption[]>(() => [
  ...(props.allowAddToDashboard
    ? [
        {
          id: 'add-to-dashboard',
          label: props.addToDashboardLabel,
          disabled: props.disabled,
        },
      ]
    : []),
  ...(props.allowExport
    ? [
        {
          id: 'export-sheet',
          label: props.exportSheetLabel,
          disabled: props.disabled || props.exportSheetBusy,
        },
        {
          id: 'download-excel',
          label: props.downloadExcelLabel,
          disabled: props.disabled || props.downloadExcelBusy,
        },
      ]
    : []),
  ...(props.allowHideChart
    ? [
        {
          id: 'hide-chart',
          label: props.hideChartLabel,
        },
      ]
    : []),
  ...(props.allowShowChart
    ? [
        {
          id: 'show-chart',
          label: props.showChartLabel,
        },
      ]
    : []),
  ...(maintenance?.available.value
    ? [
        {
          id: 'clear-cache',
          label: props.clearCacheLabel,
          disabled: maintenance.busy.value !== null,
        },
        {
          id: 'execute-analysis',
          label: props.executeAnalysisLabel,
          disabled: maintenance.busy.value !== null,
        },
      ]
    : []),
  ...(props.allowFullscreen
    ? [
        {
          id: 'fullscreen',
          label: props.fullscreen ? props.exitFullscreenLabel : props.fullscreenLabel,
        },
      ]
    : []),
])

function select(item: MenuItemOption): void {
  if (item.id === 'add-to-dashboard') emit('addToDashboard')
  else if (item.id === 'export-sheet') emit('exportSheet')
  else if (item.id === 'download-excel') emit('downloadExcel')
  else if (item.id === 'fullscreen') emit('toggleFullscreen')
  else if (item.id === 'hide-chart') emit('hideChart')
  else if (item.id === 'show-chart') emit('showChart')
  else if (item.id === 'clear-cache') void maintenance?.run('clear')
  else if (item.id === 'execute-analysis') void maintenance?.run('execute')
}
</script>

<template>
  <Menu
    v-if="items.length"
    :label="menuLabel"
    :items="items"
    placement="bottom-end"
    trigger="icon"
    size="lg"
    @select="select"
  >
    <template #trigger>
      <Icon :name="REPORT_TOOLBAR_ICONS.more" :size="20" aria-hidden="true" />
    </template>
  </Menu>
</template>
